<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'

import SearchAutocomplete from '@/components/SearchAutocomplete.vue'
import { AppIcon } from '@/components/shared'
import { useAuth } from '@/composables/useAuth'
import { useModalShell } from '@/composables/useModalShell'
import { ROLE_LABELS } from '@/constants/roles'
import NavbarFlyoutPanel from '@/layouts/partials/NavbarFlyoutPanel.vue'
import NavbarRoleSwitcher from '@/layouts/partials/NavbarRoleSwitcher.vue'
import {
    faq as faqRoute,
    home,
    login as loginRoute,
    logout as logoutRoute,
    register as registerRoute,
    story as storyRoute,
} from '@/routes'
import galleries from '@/routes/galleries'
import locations from '@/routes/locations'
import profile from '@/routes/profile'
import studio from '@/routes/studio'
import userRoutes from '@/routes/user'
import { getAvatarUrl } from '@/utils/avatar'

const page = usePage()
const currentUrl = computed(() => page.url)
// Shared by HandleInertiaRequests::share - refreshes after every router call.
const cartCount = computed(() => page.props.cart_count ?? 0)

// Flyout data shared via HandleInertiaRequests (Inertia::defer'd). Falls back
// to empty arrays if the deferred request hasn't returned yet - the panels
// then render their loading sentinel, no broken state.
const flyoutLatestGalleries = computed(() => page.props.flyout?.latestGalleries ?? [])
const flyoutTopRegions = computed(() => page.props.flyout?.topRegions ?? [])
const flyoutTrendingGalleries = computed(() => page.props.flyout?.trendingGalleries ?? [])
const flyoutTopPhotographers = computed(() => page.props.flyout?.topPhotographers ?? [])

const { user, isPhotographer, isRider, isAdmin } = useAuth()

const initials = computed(() => {
    if (!user.value) return ''
    const f = user.value.first_name?.[0] ?? ''
    const l = user.value.last_name?.[0] ?? ''
    return (f + l).toUpperCase()
})

// Public-only nav links. Role-conditional shortcuts (Studio, Garáž) have
// moved into `<NavbarRoleSwitcher>` pills so single-role and multi-role
// users see the same affordance - the pill is both the status indicator
// and the zone jump button.
// `flyout` tags a nav link as having a mega-menu panel. The string identifies
// which `<NavbarFlyoutPanel>` to open and which content slot to render below.
// Links without `flyout` render as plain text links - used for static content
// pages (Příběh, FAQ) where there's nothing to preview in a mega-menu.
const navLinks = computed(() => [
    { label: 'Místa', href: locations.index.url(), match: '/mista', flyout: 'mista' },
    { label: 'Galerie', href: galleries.index.url(), match: '/galerie', flyout: 'galerie' },
    { label: 'Příběh', href: storyRoute.url(), match: '/pribeh' },
    { label: 'FAQ', href: faqRoute.url(), match: '/faq' },
])

const isActive = (match) => currentUrl.value.startsWith(match)

// ---- Navbar flyouts ----
// Single `activeFlyout` state for both panels - guarantees only one is open
// at a time and lets the user "hand off" between them smoothly: leaving
// Místa starts a close timer, entering Galerie cancels it and starts an
// open timer for the new name. The active flyout swaps without an empty
// in-between state.
//
// Open delay (120 ms) prevents accidental opens as the user sweeps the
// cursor across the navbar. Close delay (200 ms) lets the cursor cross the
// gap between trigger link and the panel without snapping shut.
const activeFlyout = ref(null) // 'mista' | 'galerie' | null
let flyoutOpenTimer = null
let flyoutCloseTimer = null

function openFlyout(name) {
    clearTimeout(flyoutCloseTimer)
    if (activeFlyout.value === name) return
    flyoutOpenTimer = setTimeout(() => {
        activeFlyout.value = name
    }, 120)
}

function closeFlyout() {
    clearTimeout(flyoutOpenTimer)
    flyoutCloseTimer = setTimeout(() => {
        activeFlyout.value = null
    }, 200)
}

function cancelFlyoutClose() {
    clearTimeout(flyoutCloseTimer)
}

// Returns true if the link should render its "active" treatment - either
// because the current URL matches OR its flyout panel is currently open.
function isLinkActive(link) {
    return isActive(link.match) || (link.flyout && activeFlyout.value === link.flyout)
}

// Czech-locale date formatter for the flyout's latest galleries.
const flyoutDateFmt = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'long' })
function formatFlyoutDate(value) {
    if (!value) return ''
    return flyoutDateFmt.format(new Date(value))
}

// ---- Avatar dropdown (existing) ----
const menuOpen = ref(false)
const menuRef = ref(null)

function handleClickOutside(event) {
    if (menuOpen.value && menuRef.value && !menuRef.value.contains(event.target)) {
        menuOpen.value = false
    }
}

// ---- Mobile slide-down menu (new) ----
// All public + role-conditional navigation lived behind `hidden md:flex`,
// leaving sub-768 px users with only the avatar dropdown - no way to reach
// /mista, /galerie, Studio, or Login. This drawer surfaces them on mobile
// without disturbing the desktop layout.
const mobileNavOpen = ref(false)

function closeMobileNav() {
    mobileNavOpen.value = false
}

// Close on route change - applies to all open chrome (mobile drawer, avatar
// menu, flyout). Inertia exposes the URL on `page.url`.
watch(currentUrl, () => {
    closeMobileNav()
    menuOpen.value = false
    activeFlyout.value = null
    clearTimeout(flyoutOpenTimer)
    clearTimeout(flyoutCloseTimer)
})

// ESC + body scroll lock - shared with ConfirmModal / VehicleTagModal /
// PhotoZoomModal via the useModalShell composable.
useModalShell(() => mobileNavOpen.value, closeMobileNav)

// Lightweight ESC handler for the navbar flyouts - useModalShell would also
// lock body scroll, which we don't want for a hover dropdown that should
// leave the rest of the page untouched.
function handleEscape(event) {
    if (event.key === 'Escape' && activeFlyout.value !== null) {
        activeFlyout.value = null
        clearTimeout(flyoutOpenTimer)
        clearTimeout(flyoutCloseTimer)
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
})
onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleEscape)
    clearTimeout(flyoutOpenTimer)
    clearTimeout(flyoutCloseTimer)
})

function logout() {
    router.post(logoutRoute.url())
    menuOpen.value = false
    closeMobileNav()
}
</script>

<template>
    <nav class="ridesnap-navbar sticky top-0 z-50 w-full bg-zinc-950/60 backdrop-blur-xl">
        <div class="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-4">
            <div class="flex items-center gap-12">
                <Link
                    class="teko-font text-primary-container flex items-center gap-2 text-3xl font-black tracking-tighter uppercase italic"
                    :href="home.url()"
                >
                    RideSnap
                    <!-- Tiny chevron accent - small orange pip after the
                         wordmark for a bit of brand personality without
                         crowding the logo. -->
                    <span
                        class="bg-primary-container mt-1 inline-block h-1.5 w-1.5 rounded-full"
                        aria-hidden="true"
                    ></span>
                </Link>
                <div class="hidden items-center justify-center gap-8 md:flex">
                    <template
                        v-for="link in navLinks"
                        :key="link.label"
                    >
                        <div
                            class="relative"
                            v-if="link.flyout"
                            @mouseenter="openFlyout(link.flyout)"
                            @mouseleave="closeFlyout"
                        >
                            <Link
                                class="ridesnap-tab-link teko-font relative flex items-center gap-1 text-xl tracking-tighter uppercase transition-colors duration-200"
                                :href="link.href"
                                :class="
                                    isLinkActive(link)
                                        ? 'ridesnap-tab-link--active text-primary-container'
                                        : 'text-zinc-400 hover:text-zinc-100'
                                "
                            >
                                {{ link.label }}
                                <AppIcon
                                    class="text-base transition-transform duration-200"
                                    name="expand_more"
                                    :class="{ 'rotate-180': activeFlyout === link.flyout }"
                                />
                            </Link>
                        </div>

                        <Link
                            class="ridesnap-tab-link teko-font relative text-xl tracking-tighter uppercase transition-colors duration-200"
                            v-else
                            :href="link.href"
                            :class="
                                isActive(link.match)
                                    ? 'ridesnap-tab-link--active text-primary-container'
                                    : 'text-zinc-400 hover:text-zinc-100'
                            "
                        >
                            {{ link.label }}
                        </Link>
                    </template>

                    <!--
                        Subtle vertical divider - separates DISCOVERY nav
                        (Místa / Galerie / Příběh / FAQ) from UTILITY
                        controls (search + role switcher). Without this
                        the navbar reads as one undifferentiated list and
                        the user can't tell what's "where do I go" vs
                        "what am I". 1 px line, brand-muted, decorative
                        only (aria-hidden).
                    -->
                    <span
                        class="h-6 w-px bg-zinc-800"
                        aria-hidden="true"
                    />

                    <!-- Global search - lupa icon expands a frosted-glass dropdown
                         with live suggestions (3 galleries + 3 locations + 3
                         photographers). Backed by /hledat/navrhy JSON endpoint
                         with 250 ms debounce + arrow-key navigation. Full /hledat
                         page is still reachable via the "Zobrazit všechny
                         výsledky" footer link inside the dropdown.
                         Lives in the utility cluster (right of the divider on
                         md+) so it visually groups with the role switcher /
                         personal tools. Mobile gets its own instance in the
                         right cluster (next to the hamburger) below. -->
                    <SearchAutocomplete />

                    <NavbarRoleSwitcher />
                </div>
            </div>

            <div class="flex items-center gap-2 md:gap-4">
                <div class="md:hidden">
                    <SearchAutocomplete />
                </div>

                <template v-if="user">
                    <Link
                        class="teko-font bg-primary-container text-on-primary-container hover:bg-primary-container/90 hidden scale-95 items-center gap-2 rounded-md px-5 py-2 text-xl font-bold uppercase transition-all duration-100 active:opacity-80 md:flex"
                        v-if="isPhotographer"
                        :href="studio.galleries.create.url()"
                    >
                        Nahrát fotky
                    </Link>

                    <div class="flex items-center gap-3 text-zinc-400">
                        <Link
                            class="relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full ring-1 ring-zinc-800 transition-all hover:bg-zinc-800/50 hover:text-white hover:ring-2 hover:ring-orange-500/60"
                            v-if="isRider"
                            :href="profile.cart.index.url()"
                            :title="
                                cartCount > 0
                                    ? `Košík (${cartCount} ${cartCount === 1 ? 'položka' : 'položek'})`
                                    : 'Košík'
                            "
                            :aria-label="
                                cartCount > 0
                                    ? `Košík, ${cartCount} ${cartCount === 1 ? 'položka' : 'položek'}`
                                    : 'Košík (prázdný)'
                            "
                        >
                            <AppIcon name="shopping_cart" />
                            <span
                                class="bg-primary-container text-on-primary-container absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold tabular-nums shadow-md ring-2 ring-zinc-950"
                                v-if="cartCount > 0"
                                aria-hidden="true"
                            >
                                {{ cartCount > 99 ? '99+' : cartCount }}
                            </span>
                        </Link>

                        <div
                            class="relative"
                            ref="menuRef"
                        >
                            <button
                                class="bg-surface-container-high flex h-10 w-10 items-center justify-center overflow-hidden rounded-full ring-1 ring-zinc-800 transition-all hover:ring-2 hover:ring-orange-500/60"
                                type="button"
                                :aria-expanded="menuOpen"
                                aria-haspopup="menu"
                                aria-controls="navbar-avatar-menu"
                                aria-label="Uživatelské menu"
                                @click="menuOpen = !menuOpen"
                            >
                                <span class="teko-font text-primary-container text-sm font-bold uppercase">
                                    {{ initials || '-' }}
                                </span>
                            </button>

                            <!-- Dropdown -->
                            <div
                                class="bg-surface-container-low absolute top-12 right-0 w-60 overflow-hidden rounded-md border border-zinc-800 shadow-2xl"
                                id="navbar-avatar-menu"
                                v-if="menuOpen"
                                role="menu"
                            >
                                <!-- Identity block - clickable, leads to public profile -->
                                <Link
                                    class="block border-b border-zinc-800 px-4 py-3 transition-colors hover:bg-zinc-900/50"
                                    v-if="user.username"
                                    :href="userRoutes.profile.url({ user: user.username })"
                                    @click="menuOpen = false"
                                >
                                    <p class="teko-font text-on-surface text-lg leading-tight uppercase">
                                        {{ user.first_name }} {{ user.last_name }}
                                    </p>
                                    <p class="text-primary-container font-mono text-xs">@{{ user.username }}</p>
                                    <p class="text-xs text-zinc-400">{{ user.email }}</p>
                                    <div class="mt-2 flex flex-wrap gap-1">
                                        <span
                                            class="bg-primary-container/20 text-primary-container inline-block rounded px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase"
                                            v-for="r in user.roles"
                                            :key="r"
                                        >
                                            {{ ROLE_LABELS[r] ?? r }}
                                        </span>
                                    </div>
                                </Link>

                                <!-- Menu items -->
                                <nav class="p-2">
                                    <Link
                                        class="text-on-surface-variant hover:text-on-surface flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors hover:bg-zinc-800/50"
                                        v-if="isRider"
                                        :href="profile.garage.index.url()"
                                        @click="menuOpen = false"
                                    >
                                        <AppIcon
                                            class="text-base"
                                            name="two_wheeler"
                                        />
                                        Moje garáž
                                    </Link>
                                    <Link
                                        class="text-on-surface-variant hover:text-on-surface flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors hover:bg-zinc-800/50"
                                        v-if="isRider"
                                        :href="profile.orders.index.url()"
                                        @click="menuOpen = false"
                                    >
                                        <AppIcon
                                            class="text-base"
                                            name="receipt_long"
                                        />
                                        Objednávky
                                    </Link>
                                    <Link
                                        class="text-on-surface-variant hover:text-on-surface flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors hover:bg-zinc-800/50"
                                        v-if="isPhotographer"
                                        :href="studio.dashboard.url()"
                                        @click="menuOpen = false"
                                    >
                                        <AppIcon
                                            class="text-base"
                                            name="dashboard"
                                        />
                                        Studio dashboard
                                    </Link>
                                    <!-- Payouts entry removed during the free-MVP phase. -->
                                    <Link
                                        class="text-primary-container flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors hover:bg-zinc-800/50"
                                        v-if="isAdmin"
                                        href="/dashboard"
                                        @click="menuOpen = false"
                                    >
                                        <AppIcon
                                            class="text-base"
                                            name="shield_person"
                                        />
                                        Admin konzole
                                    </Link>
                                    <div class="my-1 h-px bg-zinc-800" />
                                    <Link
                                        class="text-on-surface-variant hover:text-on-surface flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors hover:bg-zinc-800/50"
                                        :href="profile.edit.url()"
                                        @click="menuOpen = false"
                                    >
                                        <AppIcon
                                            class="text-base"
                                            name="settings"
                                        />
                                        Nastavení
                                    </Link>
                                    <button
                                        class="text-on-surface-variant hover:text-error flex w-full items-center gap-3 rounded px-3 py-2 text-left text-sm transition-colors hover:bg-zinc-800/50"
                                        type="button"
                                        @click="logout"
                                    >
                                        <AppIcon
                                            class="text-base"
                                            name="logout"
                                        />
                                        Odhlásit se
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Guest: login link + register CTA. Login is text-only so
                     the only filled button competing for attention is the
                     primary action (Registrovat). -->
                <template v-else>
                    <Link
                        class="teko-font hover:text-primary-container hidden items-center text-xl font-bold tracking-tight text-zinc-400 uppercase transition-colors md:flex"
                        :href="loginRoute.url()"
                    >
                        Přihlásit
                    </Link>
                    <Link
                        class="teko-font bg-primary-container text-on-primary-container hover:bg-primary-container/90 hidden items-center gap-2 rounded-md px-5 py-2 text-xl font-bold uppercase transition-all duration-100 active:opacity-80 md:flex"
                        :href="registerRoute.url()"
                    >
                        Registrovat
                    </Link>
                </template>

                <!--
                    Mobile hamburger - matches avatar styling (40 px circle
                    ring) so the two right-cluster controls stay visually
                    coherent. Flips to "close" icon when drawer open.
                -->
                <button
                    class="text-on-surface flex h-10 w-10 items-center justify-center rounded-full ring-1 ring-zinc-800 transition-all hover:ring-2 hover:ring-orange-500/60 md:hidden"
                    type="button"
                    :aria-label="mobileNavOpen ? 'Zavřít menu' : 'Otevřít menu'"
                    :aria-expanded="mobileNavOpen"
                    aria-controls="mobile-nav-drawer"
                    @click="mobileNavOpen = !mobileNavOpen"
                >
                    <AppIcon :name="mobileNavOpen ? 'close' : 'menu'" />
                </button>
            </div>
        </div>

        <!-- Mobile drawer - flows below the navbar on small viewports. The
             body-scroll lock + ESC close are handled in the script. Click
             outside (on the dark overlay) also dismisses. -->
        <Transition
            enter-active-class="transition-opacity duration-150"
            enter-from-class="opacity-0"
            leave-active-class="transition-opacity duration-150"
            leave-to-class="opacity-0"
        >
            <div
                class="fixed inset-x-0 top-17 bottom-0 z-40 overflow-y-auto bg-zinc-950/95 backdrop-blur-xl md:hidden"
                id="mobile-nav-drawer"
                v-if="mobileNavOpen"
                role="dialog"
                aria-modal="true"
                aria-label="Hlavní menu"
                @click.self="closeMobileNav"
            >
                <div class="space-y-6 px-6 py-6">
                    <!-- Public navigation -->
                    <div class="space-y-1">
                        <p class="teko-font px-2 pb-2 text-xs font-bold tracking-widest text-zinc-400 uppercase">
                            Procházet
                        </p>
                        <Link
                            class="teko-font flex items-center gap-3 rounded-md px-3 py-3 text-2xl font-bold tracking-tight uppercase transition-colors"
                            v-for="link in navLinks"
                            :key="link.label"
                            :href="link.href"
                            :class="
                                isActive(link.match)
                                    ? 'text-primary-container bg-orange-500/10'
                                    : 'text-zinc-300 hover:bg-zinc-900'
                            "
                        >
                            {{ link.label }}
                        </Link>
                    </div>

                    <!-- Authenticated zones (role-gated) -->
                    <div
                        class="space-y-1"
                        v-if="user"
                    >
                        <p class="teko-font px-2 pb-2 text-xs font-bold tracking-widest text-zinc-400 uppercase">
                            Tvé zóny
                        </p>
                        <Link
                            class="text-on-surface-variant flex items-center gap-3 rounded-md px-3 py-3 text-base transition-colors hover:bg-zinc-900 hover:text-zinc-200"
                            v-if="isRider"
                            :href="profile.garage.index.url()"
                        >
                            <AppIcon name="two_wheeler" />
                            Moje garáž
                        </Link>
                        <Link
                            class="text-on-surface-variant flex items-center gap-3 rounded-md px-3 py-3 text-base transition-colors hover:bg-zinc-900 hover:text-zinc-200"
                            v-if="isRider"
                            :href="profile.photos.index.url()"
                        >
                            <AppIcon name="photo_library" />
                            Moje fotky
                        </Link>
                        <Link
                            class="text-on-surface-variant flex items-center gap-3 rounded-md px-3 py-3 text-base transition-colors hover:bg-zinc-900 hover:text-zinc-200"
                            v-if="isPhotographer"
                            :href="studio.dashboard.url()"
                        >
                            <AppIcon name="dashboard" />
                            Studio dashboard
                        </Link>
                        <Link
                            class="text-primary-container flex items-center gap-3 rounded-md px-3 py-3 text-base transition-colors hover:bg-zinc-900"
                            v-if="isAdmin"
                            href="/dashboard"
                        >
                            <AppIcon name="shield_person" />
                            Admin konzole
                        </Link>
                    </div>

                    <!-- Photographer upload CTA -->
                    <Link
                        class="teko-font bg-primary-container text-on-primary-container flex items-center justify-center gap-2 rounded-md px-5 py-4 text-xl font-bold uppercase transition-all hover:brightness-110 active:scale-95"
                        v-if="user && isPhotographer"
                        :href="studio.galleries.create.url()"
                    >
                        <AppIcon name="add_a_photo" />
                        Nahrát fotky
                    </Link>

                    <!-- Guest auth buttons -->
                    <div
                        class="space-y-3"
                        v-if="!user"
                    >
                        <Link
                            class="teko-font bg-primary-container text-on-primary-container flex items-center justify-center gap-2 rounded-md px-5 py-4 text-xl font-bold uppercase transition-all hover:brightness-110 active:scale-95"
                            :href="registerRoute.url()"
                        >
                            Registrovat
                        </Link>
                        <Link
                            class="teko-font hover:border-primary-container hover:text-primary-container flex items-center justify-center gap-2 rounded-md border border-zinc-700 px-5 py-4 text-xl font-bold text-zinc-300 uppercase transition-all"
                            :href="loginRoute.url()"
                        >
                            Přihlásit
                        </Link>
                    </div>
                </div>
            </div>
        </Transition>

        <!-- Místa flyout - top kraje + procházet + 2 latest galleries -->
        <NavbarFlyoutPanel
            :open="activeFlyout === 'mista'"
            aria-label="Místa - rychlá navigace"
            @mouseenter="cancelFlyoutClose"
            @mouseleave="closeFlyout"
        >
            <template #left>
                <div class="grid grid-cols-2 gap-x-8">
                    <div>
                        <p class="text-primary-container font-mono text-[11px] font-bold tracking-[0.25em] uppercase">
                            Top kraje
                        </p>
                        <div class="mt-5 flex flex-col gap-1">
                            <Link
                                class="group flex items-center justify-between gap-2 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                v-for="region in flyoutTopRegions"
                                :key="region.region"
                                :href="locations.index.url({ query: { region: region.region } })"
                            >
                                <span
                                    class="teko-font group-hover:text-on-surface text-base font-bold tracking-wide text-zinc-300 uppercase"
                                >
                                    {{ region.region }}
                                </span>
                                <span class="font-mono text-[11px] tracking-widest text-zinc-400 uppercase">
                                    {{ region.locations_count }}
                                </span>
                            </Link>
                            <!-- Skeleton rows while Inertia::defer'd flyout payload
                                 is in flight. Same row shape (region name + count)
                                 so the panel doesn't shift when real data lands. -->
                            <div
                                class="space-y-2 px-2 py-2"
                                v-if="flyoutTopRegions.length === 0"
                                aria-busy="true"
                                role="status"
                                aria-label="Načítám kraje"
                            >
                                <div
                                    class="flex items-center justify-between gap-2"
                                    v-for="n in 4"
                                    :key="n"
                                >
                                    <div class="h-3 w-24 animate-pulse rounded bg-zinc-900" />
                                    <div class="h-2 w-6 animate-pulse rounded bg-zinc-900/70" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <p class="text-primary-container font-mono text-[11px] font-bold tracking-[0.25em] uppercase">
                            Procházet
                        </p>
                        <div class="mt-5 flex flex-col gap-1">
                            <Link
                                class="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                :href="locations.index.url()"
                            >
                                <AppIcon
                                    class="text-primary-container"
                                    name="map"
                                    filled
                                />
                                <div class="min-w-0">
                                    <p
                                        class="teko-font group-hover:text-on-surface text-base font-bold tracking-wide text-zinc-200 uppercase"
                                    >
                                        Mapa fotospotů
                                    </p>
                                    <p class="text-[11px] text-zinc-400">Interaktivní mapa všech aktivních lokací</p>
                                </div>
                            </Link>
                            <Link
                                class="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                :href="locations.suggest.url()"
                            >
                                <AppIcon
                                    class="text-primary-container"
                                    name="add_location_alt"
                                    filled
                                />
                                <div class="min-w-0">
                                    <p
                                        class="teko-font group-hover:text-on-surface text-base font-bold tracking-wide text-zinc-200 uppercase"
                                    >
                                        Navrhnout místo
                                    </p>
                                    <p class="text-[11px] text-zinc-400">Přidej fotospot, který na mapě chybí</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </template>

            <template #right>
                <div>
                    <p class="text-primary-container font-mono text-[11px] font-bold tracking-[0.25em] uppercase">
                        Posledně přidané galerie
                    </p>
                    <div class="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Link
                            class="group block"
                            v-for="gallery in flyoutLatestGalleries"
                            :key="gallery.id"
                            :href="galleries.show.url({ gallery: gallery.slug })"
                        >
                            <div class="bg-surface-container-low relative overflow-hidden rounded-md">
                                <img
                                    class="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    v-if="gallery.cover_image_url"
                                    :alt="gallery.name"
                                    :src="gallery.cover_image_url"
                                    loading="lazy"
                                />
                                <div
                                    class="bg-surface-container-high flex aspect-video w-full items-center justify-center"
                                    v-else
                                >
                                    <AppIcon
                                        class="text-3xl text-zinc-700"
                                        name="image"
                                    />
                                </div>
                            </div>
                            <p
                                class="teko-font group-hover:text-primary-container mt-3 text-base font-bold tracking-wide text-zinc-200 uppercase transition-colors"
                            >
                                {{ gallery.name }}
                            </p>
                            <p class="mt-1 font-mono text-[10px] tracking-widest text-zinc-400 uppercase">
                                {{ gallery.location?.name ?? 'Bez lokace' }}
                                <span
                                    class="text-zinc-700"
                                    v-if="gallery.taken_at"
                                >
                                    ·
                                </span>
                                <span v-if="gallery.taken_at">{{ formatFlyoutDate(gallery.taken_at) }}</span>
                            </p>
                        </Link>
                        <!-- Skeleton cards while deferred latestGalleries payload is loading.
                             Same shape (aspect-video cover + title row + meta row) so the
                             panel doesn't reshuffle when real cards land. -->
                        <template v-if="flyoutLatestGalleries.length === 0">
                            <div
                                class="col-span-full grid grid-cols-1 gap-4 sm:grid-cols-2"
                                aria-busy="true"
                                role="status"
                                aria-label="Načítám nejnovější galerie"
                            >
                                <div
                                    v-for="n in 2"
                                    :key="n"
                                >
                                    <div class="aspect-video w-full animate-pulse rounded-md bg-zinc-900" />
                                    <div class="mt-3 h-3 w-3/4 animate-pulse rounded bg-zinc-900" />
                                    <div class="mt-2 h-2 w-1/2 animate-pulse rounded bg-zinc-900/70" />
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </template>
        </NavbarFlyoutPanel>

        <!-- Galerie flyout - top fotografové + sort + 2 trending galleries -->
        <NavbarFlyoutPanel
            :open="activeFlyout === 'galerie'"
            aria-label="Galerie - rychlá navigace"
            @mouseenter="cancelFlyoutClose"
            @mouseleave="closeFlyout"
        >
            <template #left>
                <div class="grid grid-cols-2 gap-x-8">
                    <div>
                        <p class="text-primary-container font-mono text-[11px] font-bold tracking-[0.25em] uppercase">
                            Top fotografové
                        </p>
                        <div class="mt-5 flex flex-col gap-1">
                            <Link
                                class="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                v-for="photographer in flyoutTopPhotographers"
                                :key="photographer.id"
                                :href="userRoutes.profile.url({ user: photographer.username })"
                            >
                                <img
                                    class="bg-surface-container-high h-9 w-9 shrink-0 rounded-full object-cover"
                                    :alt="`${photographer.first_name} ${photographer.last_name}`"
                                    :src="getAvatarUrl(photographer.id)"
                                    loading="lazy"
                                />
                                <div class="min-w-0 flex-1">
                                    <p
                                        class="teko-font group-hover:text-on-surface truncate text-base font-bold tracking-wide text-zinc-200 uppercase"
                                    >
                                        {{ photographer.first_name }} {{ photographer.last_name }}
                                    </p>
                                    <p class="font-mono text-[10px] tracking-widest text-zinc-400 uppercase">
                                        @{{ photographer.username }}
                                    </p>
                                </div>
                                <span class="font-mono text-[11px] tracking-widest text-zinc-400 uppercase">
                                    {{ photographer.galleries_count }}
                                </span>
                            </Link>
                            <!-- Skeleton rows for the deferred top-photographers payload.
                                 Matches the avatar + name + handle + count shape. -->
                            <div
                                class="space-y-1.5 px-2 py-2"
                                v-if="flyoutTopPhotographers.length === 0"
                                aria-busy="true"
                                role="status"
                                aria-label="Načítám fotografy"
                            >
                                <div
                                    class="flex items-center gap-3"
                                    v-for="n in 3"
                                    :key="n"
                                >
                                    <div class="h-9 w-9 shrink-0 animate-pulse rounded-full bg-zinc-900" />
                                    <div class="flex-1 space-y-1.5">
                                        <div class="h-3 w-1/2 animate-pulse rounded bg-zinc-900" />
                                        <div class="h-2 w-1/4 animate-pulse rounded bg-zinc-900/70" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <p class="text-primary-container font-mono text-[11px] font-bold tracking-[0.25em] uppercase">
                            Řadit podle
                        </p>
                        <div class="mt-5 flex flex-col gap-1">
                            <Link
                                class="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                :href="galleries.index.url()"
                            >
                                <AppIcon
                                    class="text-primary-container"
                                    name="schedule"
                                />
                                <span
                                    class="teko-font group-hover:text-on-surface text-base font-bold tracking-wide text-zinc-200 uppercase"
                                >
                                    Nejnovější
                                </span>
                            </Link>
                            <Link
                                class="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                :href="galleries.index.url({ query: { sort: 'oldest' } })"
                            >
                                <AppIcon
                                    class="text-primary-container"
                                    name="history"
                                />
                                <span
                                    class="teko-font group-hover:text-on-surface text-base font-bold tracking-wide text-zinc-200 uppercase"
                                >
                                    Nejstarší
                                </span>
                            </Link>
                            <Link
                                class="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                :href="galleries.index.url({ query: { sort: 'most_photos' } })"
                            >
                                <AppIcon
                                    class="text-primary-container"
                                    name="photo_library"
                                />
                                <span
                                    class="teko-font group-hover:text-on-surface text-base font-bold tracking-wide text-zinc-200 uppercase"
                                >
                                    Nejvíc fotek
                                </span>
                            </Link>
                            <Link
                                class="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-zinc-900"
                                :href="galleries.index.url({ query: { sort: 'alphabetic' } })"
                            >
                                <AppIcon
                                    class="text-primary-container"
                                    name="sort_by_alpha"
                                />
                                <span
                                    class="teko-font group-hover:text-on-surface text-base font-bold tracking-wide text-zinc-200 uppercase"
                                >
                                    Abecedně
                                </span>
                            </Link>
                        </div>
                    </div>
                </div>
            </template>

            <template #right>
                <div>
                    <p class="text-primary-container font-mono text-[11px] font-bold tracking-[0.25em] uppercase">
                        Trending galerie
                    </p>
                    <div class="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Link
                            class="group block"
                            v-for="gallery in flyoutTrendingGalleries"
                            :key="gallery.id"
                            :href="galleries.show.url({ gallery: gallery.slug })"
                        >
                            <div class="bg-surface-container-low relative overflow-hidden rounded-md">
                                <img
                                    class="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    v-if="gallery.cover_image_url"
                                    :alt="gallery.name"
                                    :src="gallery.cover_image_url"
                                    loading="lazy"
                                />
                                <div
                                    class="bg-surface-container-high flex aspect-video w-full items-center justify-center"
                                    v-else
                                >
                                    <AppIcon
                                        class="text-3xl text-zinc-700"
                                        name="image"
                                    />
                                </div>
                                <!-- Trending photo-count chip -->
                                <span
                                    class="bg-primary-container text-on-primary-container absolute top-2 right-2 rounded px-2 py-0.5 font-mono text-[10px] font-bold tracking-widest uppercase"
                                >
                                    {{ gallery.photo_count }} fotek
                                </span>
                            </div>
                            <p
                                class="teko-font group-hover:text-primary-container mt-3 text-base font-bold tracking-wide text-zinc-200 uppercase transition-colors"
                            >
                                {{ gallery.name }}
                            </p>
                            <p
                                class="mt-1 font-mono text-[10px] tracking-widest text-zinc-400 uppercase"
                                v-if="gallery.location"
                            >
                                {{ gallery.location.name }}
                            </p>
                        </Link>
                        <!-- Skeleton cards for deferred trendingGalleries payload.
                             Same aspect-video + title + meta shape as the real cards. -->
                        <template v-if="flyoutTrendingGalleries.length === 0">
                            <div
                                class="col-span-full grid grid-cols-1 gap-4 sm:grid-cols-2"
                                aria-busy="true"
                                role="status"
                                aria-label="Načítám trending galerie"
                            >
                                <div
                                    v-for="n in 2"
                                    :key="n"
                                >
                                    <div class="aspect-video w-full animate-pulse rounded-md bg-zinc-900" />
                                    <div class="mt-3 h-3 w-3/4 animate-pulse rounded bg-zinc-900" />
                                    <div class="mt-2 h-2 w-1/3 animate-pulse rounded bg-zinc-900/70" />
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </template>
        </NavbarFlyoutPanel>
    </nav>
</template>
